import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  SelectRoot,
  SelectButton,
  SelectValue,
  SelectChevron,
  SelectPopover,
  SelectList,
  SelectOption,
  SelectEmpty,
} from "./styles";

export type SelectOptionType = {
  value: string;
  label: string;
};

type Props = {
  value: string; // value selecionado
  options: SelectOptionType[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
};

export default function CustomSelect({
  value,
  options,
  placeholder = "Selecione",
  disabled,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(() => {
    const idx = options.findIndex((o) => o.value === value);
    return idx >= 0 ? idx : 0;
  });

  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  // fecha ao clicar fora
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (!open) return;
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) setOpen(false);
    }

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  // mantém activeIndex alinhado com o value
  useEffect(() => {
    const idx = options.findIndex((o) => o.value === value);
    if (idx >= 0) setActiveIndex(idx);
  }, [value, options]);

  // scroll até item ativo quando abre
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  function selectAt(index: number) {
    const opt = options[index];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
  }

  function toggle() {
    if (disabled) return;
    setOpen((v) => !v);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else {
        selectAt(activeIndex);
      }
      return;
    }

    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, options.length - 1));
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
  }

  return (
    <SelectRoot ref={rootRef}>
      <SelectButton
        type="button"
        onClick={toggle}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <SelectValue $muted={!selected}>{selected ? selected.label : placeholder}</SelectValue>
        <SelectChevron $open={open} />
      </SelectButton>

      {open && (
        <SelectPopover role="listbox" aria-activedescendant={`opt-${activeIndex}`}>
          <SelectList ref={listRef}>
            {options.length === 0 ? (
              <SelectEmpty>Nenhuma opção</SelectEmpty>
            ) : (
              options.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isActive = idx === activeIndex;

                return (
                  <SelectOption
                    key={opt.value}
                    id={`opt-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    $active={isActive}
                    $selected={isSelected}
                    data-index={idx}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onMouseDown={(ev) => {
                      // evita perder foco antes do click
                      ev.preventDefault();
                      selectAt(idx);
                    }}
                  >
                    {opt.label}
                  </SelectOption>
                );
              })
            )}
          </SelectList>
        </SelectPopover>
      )}
    </SelectRoot>
  );
}
